import { RadioGroup } from '@headlessui/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useActionData, useLoaderData, useNavigate } from 'react-router-dom'

import { Breadcrumb } from '@/components/Breadcrumb.tsx'
import { StyledActionButton } from '@/components/Buttons.tsx'
import { Stepper } from '@/components/Stepper.tsx'
import { FormError } from '@/components/templates/Form.tsx'
import {
  LocationCard,
  LocationRadio,
} from '@/components/templates/LocationCard.tsx'
import {
  ButtonGroup,
  FullScreenContainer,
  ScrollableContent,
} from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Header1 } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { ActionData } from '@/screens/xpert/BookWithReferral/actions.ts'
import { useAppointmentStore } from '@/store.ts'
import { XpertFormStep } from '@/utils/XpertFormSteps.ts'

import { StepEnum, TOTAL_STEPS } from '../constants.ts'
import { LoaderData } from './actions.ts'

const SelectClinic = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'selectClinic',
  })
  const navigate = useNavigate()
  const actionData = useActionData() as ActionData | undefined
  const { loadTime, locations } = useLoaderData() as LoaderData
  const { selectedLocation, setSelectedLocation } = useAppointmentStore(
    (state) => ({
      selectedLocation: state.selectedLocation,
      setSelectedLocation: state.setSelectedLocation,
    }),
  )

  const { trackFormError, trackFormInteraction } = useTrackFormProgress(
    XpertFormStep.SelectClinic,
    { loadTime },
  )

  useEffect(() => {
    if (actionData?.error) {
      trackFormError({
        error: actionData.error,
      })
    }
  }, [actionData?.error])

  const handleSelectLocation = (selectedLocationId: string) => {
    const location = locations?.find(
      (location) => location.emmaId === selectedLocationId,
    )

    if (location) {
      setSelectedLocation(location)
    }

    trackFormInteraction({
      action: 'click',
      formFieldName: 'location',
      formFieldValue: location?.name,
      formFieldValues: locations?.map((location) => location.name || ''),
    })
  }

  if (!locations) {
    return null
  }

  return (
    <ScreenWrapper>
      <FullScreenContainer>
        <ScrollableContent>
          <Breadcrumb
            id="back-to-previous"
            onClick={() => {
              navigate(-1)
              trackFormInteraction({
                action: 'previous',
                formFieldName: 'back-to-previous',
              })
            }}
            title={t('breadcrumbLink')}
          />
          <Header1>{t('header')}</Header1>
          <Stepper total={TOTAL_STEPS} step={StepEnum.clinic} />
          <RadioGroup
            name="selectedLocation"
            value={selectedLocation?.emmaId}
            onChange={handleSelectLocation}
          >
            {locations?.map((location) => (
              <LocationRadio value={location.emmaId} key={location.emmaId}>
                <LocationCard location={location} />
              </LocationRadio>
            ))}
          </RadioGroup>

          {actionData?.error && <FormError>{actionData.error}</FormError>}
        </ScrollableContent>

        <ButtonGroup>
          <StyledActionButton
            variant="Primary"
            type="submit"
            onClick={() => {
              navigate('/referral/manual-booking/select-date-time')
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
            }}
          >
            {t('actions.selectTimeSlot')}
          </StyledActionButton>
        </ButtonGroup>
      </FullScreenContainer>
    </ScreenWrapper>
  )
}

export { SelectClinic }
